import {
  faTrash,
  faUser,
  faUserPlus,
  faUserMinus,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import type z from "zod";
import { schemas } from "../../api/generated";
import { ActivityDate } from "../ActivityDate";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type PaginationState,
  type Updater,
  type SortingState,
  type CellContext,
} from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { useMemo } from "react";

type User = z.infer<typeof schemas.User>;

export type UsersTableProps = {
  data: User[];
  totalUsers: number;
  // eslint-disable-next-line no-unused-vars
  setSelectedUser: (user: User) => void;
  // eslint-disable-next-line no-unused-vars
  setShowConfirmation: (show: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onPromoteUser: (user: User) => void;
  // eslint-disable-next-line no-unused-vars
  onDemoteUser: (user: User) => void;
  canPromoteUsers: boolean;
  /** Logged-in user's id, used to hide the demote action on your own row. */
  currentUserId?: string | undefined;
  onTableChange: (
    // eslint-disable-next-line no-unused-vars
    pagination: Updater<PaginationState>,
    // eslint-disable-next-line no-unused-vars
    sorting: Updater<SortingState>
  ) => void;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
};

function DateCell(info: CellContext<User, unknown>) {
  return <ActivityDate date={info.getValue<string>()} />;
}

/**
 * Role column cell: a status icon (shield = Admin, plain person = User) before
 * the role label. The label carries the meaning; the icon is decorative (Font
 * Awesome marks it aria-hidden by default) and just aids quick scanning.
 */
function RoleCell(info: CellContext<User, unknown>) {
  const role = info.getValue<string>();
  const isAdmin = role === "Admin";
  return (
    <span className="text-nowrap">
      <FontAwesomeIcon
        icon={isAdmin ? faUserShield : faUser}
        className="me-1 text-secondary"
      />
      {role}
    </span>
  );
}

/**
 * A single admin role-management action button (promote or demote). Shares the
 * tooltip + disabled-state scaffold; the caller supplies the icon, label and
 * handler. The span wrapper keeps the tooltip working while the button is
 * disabled.
 */
function RoleActionButton({
  tooltipId,
  icon,
  label,
  enabled,
  disabledReason,
  onClick,
}: {
  tooltipId: string;
  icon: IconDefinition;
  label: string;
  enabled: boolean;
  disabledReason: string;
  onClick: () => void;
}) {
  return (
    <OverlayTrigger
      overlay={
        <Tooltip id={tooltipId}>{enabled ? label : disabledReason}</Tooltip>
      }
    >
      <span className="d-inline-block">
        <Button
          variant="outline-dark"
          size="sm"
          aria-label={label}
          disabled={!enabled}
          style={enabled ? undefined : { pointerEvents: "none" }}
          onClick={onClick}
        >
          <FontAwesomeIcon width={20} icon={icon} />
        </Button>
      </span>
    </OverlayTrigger>
  );
}

export function UsersTable({
  data,
  totalUsers,
  setSelectedUser,
  setShowConfirmation,
  onPromoteUser,
  onDemoteUser,
  canPromoteUsers,
  currentUserId,
  onTableChange,
  pageIndex,
  pageSize,
  sortBy,
  sortDescending,
}: UsersTableProps) {
  const tableData = data || [];

  const columns: ColumnDef<User, unknown>[] = useMemo(
    () => [
      {
        id: "first name",
        accessorKey: "firstname",
        meta: { sortKey: "firstname" },
        header: () => "First Name",
        cell: (info) => info.getValue<string>(),
        size: 77,
        minSize: 66,
        maxSize: 120,
      },
      {
        id: "last name",
        accessorKey: "lastname",
        meta: { sortKey: "lastname" },
        header: () => "Last Name",
        cell: (info) => info.getValue<string>(),
        size: 150,
        minSize: 66,
        maxSize: 150,
      },
      {
        id: "email address",
        accessorKey: "email",
        meta: { sortKey: "email" },
        header: () => "Email",
        cell: (info) => info.getValue<string>(),
        size: 180,
        minSize: 180,
        maxSize: 400,
      },
      {
        id: "username",
        accessorKey: "username",
        meta: { sortKey: "username" },
        header: () => "Username",
        cell: (info) => info.getValue<string>(),
        size: 120,
        minSize: 120,
        maxSize: 280,
      },
      {
        id: "role",
        accessorKey: "userType",
        meta: { sortKey: "userType" },
        header: () => "Role",
        cell: RoleCell,
        size: 90,
      },
      {
        id: "file name",
        accessorFn: (row) => row.files[0],
        meta: { sortKey: "files" },
        header: () => "Assigned Library",
        cell: (info) => {
          const file = info.getValue<string>();
          return (
            <span style={{ maxWidth: "200px" }}>
              <code className="text-body d-block" title={file}>
                {file}
              </code>
            </span>
          );
        },
        minSize: 150,
      },
      {
        id: "registered date",
        accessorKey: "createdAt",
        meta: { sortKey: "createdAt" },
        header: () => (
          <span title="The date and time the user account was created">
            Registered
          </span>
        ),
        cell: DateCell,
        size: 106,
      },
      {
        id: "last login failed date",
        accessorKey: "loginFailedAt",
        meta: { sortKey: "loginFailedAt" },
        header: () => (
          <span title="The date and time when the user made a failed login attempt">
            Login Failed
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 114,
      },
      {
        id: "last login succeeded date",
        accessorKey: "loginSucceededAt",
        meta: { sortKey: "loginSucceededAt" },
        header: () => (
          <span title="The date and time that the user successfully logged in">
            Login Succeeded
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 149,
      },
      {
        id: "last attack library downloaded date",
        accessorKey: "attackLibraryDownloadedAt",
        meta: { sortKey: "attackLibraryDownloadedAt" },
        header: () => (
          <span title="The date and time the attack library was last downloaded">
            Last Download
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 132,
      },
      {
        id: "actions",
        header: () => "Actions",
        enableSorting: false,
        cell: ({ row }) => {
          const user = row.original;

          return (
            <div
              className="d-flex justify-content-center gap-1"
              style={{
                // Work around for built in cell padding
                marginTop: "-6px",
                marginBottom: "-6px",
              }}
            >
              {/* Role action: promote (Users) or demote (Admins). Every row
                  shows exactly one, kept enabled/disabled rather than hidden so
                  the column layout stays consistent and the reason is explained
                  in the tooltip. */}
              {user.userType === "User" ? (
                <RoleActionButton
                  tooltipId={`promote-${user.id}`}
                  icon={faUserPlus}
                  label="Promote user to Admin"
                  disabledReason="Admin promotion is currently disabled for this environment"
                  enabled={canPromoteUsers}
                  onClick={() => onPromoteUser(user)}
                />
              ) : (
                <RoleActionButton
                  tooltipId={`demote-${user.id}`}
                  icon={faUserMinus}
                  label="Demote Admin to User"
                  disabledReason={
                    user.id === currentUserId
                      ? "You can't demote your own account"
                      : "Admin role management is currently disabled for this environment"
                  }
                  enabled={canPromoteUsers && user.id !== currentUserId}
                  onClick={() => onDemoteUser(user)}
                />
              )}
              {/* Delete is disabled (not hidden) on your own row; the backend
                  also rejects self-deletion. */}
              <RoleActionButton
                tooltipId={`delete-${user.id}`}
                icon={faTrash}
                label="Delete user"
                disabledReason="You can't delete your own account"
                enabled={user.id !== currentUserId}
                onClick={() => {
                  setSelectedUser(user);
                  setShowConfirmation(true);
                }}
              />
            </div>
          );
        },
        meta: {
          width: "min-content",
        },
      },
    ],
    [
      canPromoteUsers,
      currentUserId,
      onPromoteUser,
      onDemoteUser,
      setSelectedUser,
      setShowConfirmation,
    ]
  );

  const table = useReactTable<User>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (pagination) => {
      onTableChange(pagination, (prev) => prev);
    },
    onSortingChange: (sorting) => {
      const newSorting =
        typeof sorting !== "function"
          ? sorting
          : sorting(toSorting(sortBy, sortDescending));
      const sortKey = toSortKey(columns, newSorting[0]?.id);
      const modifiedSorting = toSorting(sortKey, newSorting[0]?.desc);
      onTableChange((prev) => ({ ...prev, pageIndex: 0 }), modifiedSorting);
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: totalUsers,
    state: {
      pagination: { pageIndex, pageSize },
      sorting: toSorting(sortBy, sortDescending),
    },
    defaultColumn: {
      size: Number.NaN,
      minSize: Number.NaN,
      maxSize: Number.NaN,
    },
  });

  return <DataTable table={table} itemName="user" />;
}

function toSortKey<T>(columns: ColumnDef<T>[], colId: string) {
  const column = columns.find((col) => col.id === colId);
  if (!column) return colId;
  return column.meta && "sortKey" in column.meta
    ? (column?.meta?.sortKey as string | undefined)
    : colId;
}

function toSorting(sortBy?: string, sortDescending?: boolean) {
  return sortBy
    ? [
        {
          id: sortBy,
          desc: sortDescending || false,
        },
      ]
    : [];
}
